// src/pages/EmployeeDashboardPage.tsx
// Refactored with Precision with Warmth design system

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthAdapter';
import { SidebarPortal } from '../../shared/components/SidebarPortal';
import { Project, Task, TimeEntry } from '../services/api/types';
import { apiService } from '../services/api';
import { useApi } from '../hooks/useApi';
import TaskManagement from '../components/TaskManagement';
import ConfirmationModal from '../components/ConfirmationModal';
import CreateProjectModal from '../components/CreateProjectModal';
import { Button, IconButton, Input, Spinner, EmptyState } from '../../shared/components/ui';
import './EmployeeDashboardPage.css';

// Formata uma data no fuso LOCAL como YYYY-MM-DD (evita o off-by-one do toISOString em UTC)
function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const EmployeeDashboardPage: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [todayEntries, setTodayEntries] = useState<TimeEntry[]>([]);
  const [selectedDate, setSelectedDate] = useState(ymd(new Date()));
  const [availableEmployees, setAvailableEmployees] = useState<{ id: number; name: string }[]>([]);

  const [currentWeek, setCurrentWeek] = useState(getStartOfWeek(new Date()));
  const [tasksLoading, setTasksLoading] = useState(false);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [projectQuery, setProjectQuery] = useState('');
  const [weekHoursByDay, setWeekHoursByDay] = useState<Record<string, number>>({});

  // Confirmation modal states
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    type: 'complete' | 'delete' | null;
    project: Project | null;
  }>({
    isOpen: false,
    type: null,
    project: null
  });

  // Check if user can create projects (managers, directors, admins)
  const canCreateProject = user?.role && (
    ['manager', 'director', 'admin', 'project manager'].includes(user.role.toLowerCase()) ||
    user.role.toLowerCase().includes('manager') ||
    user.role.toLowerCase().includes('director') ||
    user.role.toLowerCase().includes('admin') ||
    user.role === 'Project Manager'
  );

  // Handle project creation
  const handleProjectCreated = async () => {
    setIsProjectModalOpen(false);
    // Refresh projects list
    fetchProjects();
  };

  // Use API hook for projects
  const {
    data: projects,
    loading: projectsLoading,
    error: projectsError,
    execute: fetchProjects
  } = useApi(
    () => user ? apiService.projects.getProjects() : Promise.resolve([]),
    [user?.id],
    { immediate: !!user }
  );

  // Navigation items are now fixed based on user role

  // Auto-select first project when projects load
  useEffect(() => {
    if (projects && Array.isArray(projects) && projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject]);

  // Fetch tasks when project, user, or selected date changes
  useEffect(() => {
    if (selectedProject && user) {
      fetchProjectTasks();
    }
  }, [selectedProject, user?.id, selectedDate]);

  // Fetch time entries when date, project, or user changes
  useEffect(() => {
    if (user) {
      fetchTodayEntries();
    }
  }, [selectedDate, user?.id]);

  // Busca as horas registradas em cada dia da semana (para exibir na faixa do calendário)
  useEffect(() => {
    if (!user) return;
    let active = true;
    const days = getWeekDays();
    Promise.all(
      days.map((d) => apiService.timeEntries.getDailyTimeEntries(user.id, ymd(d)).catch(() => []))
    ).then((results) => {
      if (!active) return;
      const map: Record<string, number> = {};
      days.forEach((d, i) => {
        const arr = Array.isArray(results[i]) ? results[i] : [];
        map[ymd(d)] = arr.reduce((s: number, e: TimeEntry) => s + (e.hours || 0), 0);
      });
      setWeekHoursByDay(map);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeek, user?.id, selectedDate]);

  // Fetch available employees when project changes (for managers/supervisors creating tasks)
  useEffect(() => {
    if (selectedProject && user && user.role !== 'Employee') {
      fetchAvailableEmployees();
    }
  }, [selectedProject, user]);

  const fetchAvailableEmployees = async () => {
    if (!selectedProject || !user || user.role === 'Employee') return;

    try {
      // Get all users to find employee names
      const allUsers = await apiService.users.getUsers();

      // Map project employee IDs to user info
      const assignedIds = selectedProject.assigned_employee_ids || [];
      const employeeList = assignedIds
        .map(empId => {
          const emp = allUsers.find(u => u.id === empId);
          return emp ? { id: emp.id, name: emp.name } : null;
        })
        .filter(emp => emp !== null) as { id: number; name: string }[];

      setAvailableEmployees(employeeList);
    } catch (error) {
      setAvailableEmployees([]);
    }
  };

  const fetchProjectTasks = async () => {
    if (!user || !selectedProject) return;

    setTasksLoading(true);
    try {
      // Get all tasks for the project and user - use employee_id if available, fallback to user.id
      const userIdForQuery = user.employee_id || user.id;
      const allTasks = await apiService.tasks.getTasksByUserAndProject(userIdForQuery, selectedProject.id);
      const tasksArray = Array.isArray(allTasks) ? allTasks : [];

      // Filter tasks by selected date
      const filteredTasks = tasksArray.filter(task => {
        if (!task.task_date) return true; // Show tasks without dates
        try {
          const taskDateStr = typeof task.task_date === 'string'
            ? task.task_date.split('T')[0]
            : new Date(task.task_date).toISOString().split('T')[0];
          return taskDateStr === selectedDate;
        } catch (e) {
          return true; // Show task if date parsing fails
        }
      });

      setTasks(filteredTasks);
    } catch (error) {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchTodayEntries = async () => {
    if (!user) return;

    setEntriesLoading(true);
    try {
      const entries = await apiService.timeEntries.getDailyTimeEntries(user.id, selectedDate);
      setTodayEntries(Array.isArray(entries) ? entries : []);
    } catch (error) {
      console.error('Error fetching time entries:', error);
      setTodayEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
  };

  const handleTaskUpdated = () => {
    fetchProjectTasks();
    fetchTodayEntries();
  };

  const handleTimeLogged = () => {
    fetchTodayEntries();
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newWeek);
  };

  const getTotalHoursForDay = (): number => {
    if (!Array.isArray(todayEntries)) return 0;
    return todayEntries.reduce((total, entry) => total + entry.hours, 0);
  };

  // Confirmation modal handlers
  const handleCompleteProject = (project: Project) => {
    setConfirmationModal({
      isOpen: true,
      type: 'complete',
      project: project
    });
  };

  const handleDeleteProject = (project: Project) => {
    setConfirmationModal({
      isOpen: true,
      type: 'delete',
      project: project
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmationModal.project) return;

    try {
      if (confirmationModal.type === 'complete') {
        await apiService.projects.completeProject(confirmationModal.project.id);
      } else if (confirmationModal.type === 'delete') {
        await apiService.projects.deleteProject(confirmationModal.project.id);
      }

      // Refresh projects list
      fetchProjects();

      // Clear selection if this was the selected project
      if (selectedProject?.id === confirmationModal.project.id) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error(`Error ${confirmationModal.type}ing project:`, error);
      alert(`Erro ao ${confirmationModal.type === 'complete' ? 'concluir' : 'deletar'} projeto. Tente novamente.`);
    }
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      type: null,
      project: null
    });
  };

  // Generate calendar days for the current week
  const getWeekDays = () => {
    const days = [];
    const startDate = new Date(currentWeek);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }

    return days;
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelectedDate = (date: Date): boolean => {
    return ymd(date) === selectedDate;
  };

  // Helper function to create date from string without timezone issues
  const createLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const projectsList = Array.isArray(projects) ? projects : [];
  const filteredProjects = projectQuery.trim()
    ? projectsList.filter((p: Project) => p.name.toLowerCase().includes(projectQuery.trim().toLowerCase()))
    : projectsList;

  const sidebarContent = (
    <>
        {/* Create New Project Button - Only for managers/admins */}
        {canCreateProject && (
          <Button
            variant="primary"
            fullWidth
            className="mb-4"
            onClick={() => setIsProjectModalOpen(true)}
            data-testid="project-create-button"
            leftIcon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            }
          >
            Novo Projeto
          </Button>
        )}

        {/* Project Selection */}
        <div className="mb-6">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground-muted">
            Seus Projetos ({projectsList.length})
          </h3>

          {projectsList.length > 6 && (
            <Input
              type="search"
              placeholder="Buscar projeto..."
              value={projectQuery}
              onChange={(e) => setProjectQuery(e.target.value)}
              containerClassName="mb-3"
              className="h-9"
              data-testid="project-search-input"
              leftIcon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              }
            />
          )}

          {projectsList.length === 0 ? (
            <div className="rounded-lg border border-border bg-surface-3 p-4 text-center">
              <p className="text-sm text-foreground-muted">Nenhum projeto atribuído ainda</p>
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {filteredProjects.map((project: Project) => {
                const isSelected = selectedProject?.id === project.id;
                return (
                  <div
                    key={project.id}
                    data-testid="project-row"
                    className={`flex items-center justify-between gap-2 rounded-lg border-l-[3px] px-3 py-2.5 transition-all duration-150 ${
                      isSelected
                        ? 'border-l-brand bg-brand/10'
                        : 'border-l-transparent hover:bg-surface-3'
                    }`}
                  >
                    <button
                      onClick={() => handleProjectSelect(project)}
                      data-testid="project-select-button"
                      className={`flex flex-1 items-center gap-2 truncate rounded text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40 ${
                        isSelected ? 'font-semibold text-brand' : 'font-medium text-foreground-muted hover:text-foreground'
                      }`}
                    >
                      {project.name}
                    </button>

                    {/* Show complete/delete buttons only if user is the project creator */}
                    {project.created_by === user?.id && (
                      <div className="flex shrink-0 items-center gap-1">
                        <IconButton
                          aria-label="Concluir projeto"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompleteProject(project);
                          }}
                          data-testid="project-complete-button"
                          className="text-success hover:bg-success/15"
                          icon={
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          }
                        />
                        <IconButton
                          aria-label="Deletar projeto"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(project);
                          }}
                          data-testid="project-delete-button"
                          className="text-danger hover:bg-danger/15"
                          icon={
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="px-1 py-3 text-sm text-foreground-muted">Nenhum projeto encontrado.</p>
          )}
        </div>

        {/* Selected Date Summary */}
        <div className="rounded-lg border border-brand/30 bg-brand/10 p-4">
          <h4 className="mb-2 text-sm font-semibold text-brand">
            {isToday(createLocalDate(selectedDate)) ? 'Resumo de Hoje' : 'Data Selecionada'}
          </h4>
          <p className="font-display text-2xl font-bold text-foreground tabular-nums">
            {entriesLoading ? 'Carregando...' : `${getTotalHoursForDay()}h`}
          </p>
          <p className="mt-0.5 text-xs text-foreground-muted">
            {createLocalDate(selectedDate).toLocaleDateString('pt-BR', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
    </>
  );

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h3 className="font-display text-lg font-semibold text-foreground">Faça login para acessar seu painel.</h3>
      </div>
    );
  }

  if (projectsLoading) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <Spinner size="lg" />
        <h3 className="font-display text-lg font-semibold text-foreground">Carregando seus projetos...</h3>
      </div>
    );
  }

  if (projectsError) {
    return (
      <>
        <SidebarPortal>{sidebarContent}</SidebarPortal>
        <EmptyState
          className="m-8 py-20"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          }
          title="Erro ao carregar projetos"
          description={projectsError.message}
          action={
            <Button variant="primary" onClick={fetchProjects}>
              Tentar novamente
            </Button>
          }
        />
      </>
    );
  }

  return (
    <>
      <SidebarPortal>{sidebarContent}</SidebarPortal>

        {/* Header */}
        <div className="border-b border-border bg-surface-1 px-8 py-6">
          <h1 className="font-display text-2xl font-bold text-foreground">Meu Painel</h1>
          <p className="mt-1 text-base text-foreground-muted">
            {selectedProject ? `Trabalhando em: ${selectedProject.name}` : 'Selecione um projeto para começar'}
          </p>
        </div>

        {selectedProject ? (
          <div className="p-8">
            {/* Week Calendar */}
            <div className="mb-8 overflow-hidden rounded-xl border border-border bg-surface-1 shadow-xs">
              {/* Calendar Header */}
              <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigateWeek('prev')}
                  leftIcon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  }
                >
                  Semana anterior
                </Button>

                <h2 className="font-display text-lg font-semibold text-foreground">
                  {currentWeek.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                </h2>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigateWeek('next')}
                  rightIcon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  }
                >
                  Próxima semana
                </Button>
              </div>

              {/* Calendar Days header */}
              <div className="grid grid-cols-7 border-b border-border">
                {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((day, index) => (
                  <div
                    key={day}
                    className={`bg-surface-2 px-4 py-3 text-center text-sm font-semibold text-foreground-muted ${
                      index < 6 ? 'border-r border-border' : ''
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {getWeekDays().map((date, index) => {
                  const selected = isSelectedDate(date);
                  const today = isToday(date);
                  const dayHours = weekHoursByDay[ymd(date)] || 0;
                  return (
                    <button
                      key={date.toISOString()}
                      data-testid="calendar-day-button"
                      onClick={() => setSelectedDate(ymd(date))}
                      className={`flex flex-col items-center gap-1 px-4 py-4 text-center transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40 ${
                        index < 6 ? 'border-r border-border' : ''
                      } ${
                        selected
                          ? 'bg-brand text-brand-fg'
                          : today
                            ? 'bg-brand/10 text-brand hover:bg-brand/15'
                            : 'text-foreground hover:bg-surface-2'
                      }`}
                    >
                      <span className={`text-xl tabular-nums ${today || selected ? 'font-semibold' : 'font-normal'}`}>
                        {date.getDate()}
                      </span>
                      {today && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Hoje</span>
                      )}
                      <span
                        className={`text-xs tabular-nums ${
                          dayHours > 0
                            ? selected
                              ? 'text-brand-fg/90 font-medium'
                              : 'text-foreground-muted font-medium'
                            : selected
                              ? 'text-brand-fg/50'
                              : 'text-foreground-subtle'
                        }`}
                      >
                        {dayHours > 0 ? `${dayHours}h` : '—'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Task Management */}
            <div className="overflow-hidden rounded-xl border border-border bg-surface-1 shadow-xs">
              <div className="border-b border-border px-6 py-4">
                <h3 className="font-display text-lg font-semibold text-foreground">
                  Tarefas & Controle de Horas
                </h3>
                <p className="mt-0.5 text-sm text-foreground-muted">
                  Gerencie suas tarefas de {createLocalDate(selectedDate).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>

              <div className="p-6">
                <TaskManagement
                  project={selectedProject}
                  tasks={tasks}
                  selectedDate={selectedDate}
                  todayEntries={todayEntries}
                  onTaskUpdated={handleTaskUpdated}
                  onTimeLogged={handleTimeLogged}
                  loading={tasksLoading}
                  availableEmployees={availableEmployees}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="px-8 py-12">
            {projectsList.length === 0 ? (
              <EmptyState
                className="py-16"
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                    <path d="M14 2v6h6" />
                  </svg>
                }
                title="Nenhum projeto atribuído"
                description="Você ainda não foi atribuído a nenhum projeto. Fale com seu gestor de projeto para começar com seu primeiro projeto."
              />
            ) : (
              <EmptyState
                className="py-16"
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" />
                  </svg>
                }
                title="Selecione um projeto"
                description="Escolha um projeto na barra lateral para ver suas tarefas, controlar horas e gerenciar seu fluxo de trabalho diário."
              />
            )}
          </div>
        )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirmAction}
        title={confirmationModal.type === 'complete' ? 'Concluir Projeto' : 'Deletar Projeto'}
        message={
          confirmationModal.type === 'complete'
            ? 'Tem certeza que deseja marcar este projeto como concluído? O projeto será removido da lista de projetos ativos.'
            : 'Tem certeza que deseja deletar este projeto permanentemente? Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.'
        }
        confirmText={confirmationModal.type === 'complete' ? 'Sim, Concluir' : 'Sim, Deletar'}
        cancelText="Cancelar"
        type={confirmationModal.type === 'complete' ? 'info' : 'danger'}
        projectName={confirmationModal.project?.name}
      />

      {/* Create Project Modal */}
      {isProjectModalOpen && (
        <CreateProjectModal
          onClose={() => setIsProjectModalOpen(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}
    </>
  );
};

// Helper function to get start of week (Monday)
function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

export default EmployeeDashboardPage;
